// Session detail. Tabs:
//   1. Overview     — summary recap + actions (reschedule/cancel for upcoming)
//   2. Prescription — print-ready Rx (when canViewPrescription)
//   3. Notes        — notes the practitioner shared
//   4. Receipt      — printable receipt (when canDownloadReceipt)
//   5. Feedback     — rate + edit feedback (past sessions only)
//
// Tabs whose data isn't applicable for this session collapse — we don't
// render them. Same pattern as Today widgets.

import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import {
  BackLink,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  Pill,
  PrimaryButton,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { useSession } from '../../hooks/useSessions'
import { usePatient } from '../../context/PatientContext'
import * as shopSvc from '../../services/shopService'
import RescheduleDialog from './components/RescheduleDialog'
import CancelDialog from './components/CancelDialog'
import FeedbackDialog from './components/FeedbackDialog'
import { parseUtcDate } from '../../utils/dates'
import { useReceiptDrawer } from '../../hooks/useReceiptDrawer'
import ReceiptDrawer from '../../components/ReceiptDrawer'

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  margin-bottom: 24px;
  flex-wrap: wrap;
`

const TabBtn = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  padding: 10px 14px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${(p) =>
    p.$active ? p.theme?.color?.primary || '#1F3A5F' : p.theme?.color?.inkMuted || '#6B7280'};
  cursor: pointer;
  border-bottom: 2px solid
    ${(p) => (p.$active ? p.theme?.color?.primary || '#1F3A5F' : 'transparent')};
  transition: all 200ms ease-out;
  &:hover:not(:disabled) {
    color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  }
  &:disabled {
    color: rgba(0, 0, 0, 0.2);
    cursor: not-allowed;
  }
`

const Sheet = styled.section`
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 16px;
  padding: 28px;
`

function whenLabel(iso) {
  if (!iso) return ''
  const d = parseUtcDate(iso)
  return d.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatPaymentAmount(minor, currency = 'INR') {
  const major = (minor || 0) / 100
  if (currency === 'INR') return `₹${major.toLocaleString('en-IN')}`
  if (currency === 'USD') return `$${major.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  return `${currency || ''} ${major}`.trim()
}

export default function SessionDetailPage() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { currentProfile } = usePatient()
  const { detail, loading, reschedule, cancel: doCancel, submitFeedback, refresh } = useSession(eventId)
  const [tab, setTab] = useState('overview')
  const [reschedOpen, setReschedOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [paying, setPaying] = useState(false)
  const [payError, setPayError] = useState(null)
  const receiptDrawer = useReceiptDrawer()

  if (loading && !detail) {
    return (
      <ContentMaxWidth>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <LoadingSpinner />
        </div>
      </ContentMaxWidth>
    )
  }

  if (!detail) {
    return (
      <ContentMaxWidth>
        <Stack $gap="20px">
          <PageHeading>Session not found</PageHeading>
          <GhostButton onClick={() => navigate('/patient-home/sessions')}>
            Back to sessions
          </GhostButton>
        </Stack>
      </ContentMaxWidth>
    )
  }

  const { summary } = detail
  const isPast = summary.status === 'COMPLETED'
  // Trust the server's join window — canJoinNow already accounts for
  // the [start - 10min, end + 15min] window plus status/mode gates.
  const canJoin = !!summary.canJoinNow

  const tabs = [
    { id: 'overview', label: 'Overview', show: true },
    { id: 'prescription', label: 'Prescription', show: detail.canViewPrescription },
    { id: 'notes', label: 'Notes', show: detail.sharedNoteIds?.length > 0 },
    { id: 'receipt', label: 'Receipt', show: detail.canDownloadReceipt },
    { id: 'feedback', label: 'Feedback', show: isPast },
  ].filter((t) => t.show)

  const activeTab = tabs.find((t) => t.id === tab) ? tab : 'overview'

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/sessions">Back to sessions</BackLink>

        <Row $between $wrap $gap="12px">
          <div>
            <Row $gap="6px" $wrap>
              <Pill>{isPast ? 'Past session' : 'Upcoming'}</Pill>
              {summary.paymentBlocked && <Pill $tone="urgent">Awaiting payment</Pill>}
            </Row>
            <PageHeading style={{ marginTop: 12 }}>{summary.title}</PageHeading>
            <Muted>
              {summary.practitionerName}
              {summary.practitionerSpecialty ? ` · ${summary.practitionerSpecialty}` : ''} ·{' '}
              {whenLabel(summary.scheduledAt)} · {summary.durationMinutes} min
            </Muted>
            {payError && (
              <Muted style={{ color: '#C24D49', marginTop: 6 }}>{payError}</Muted>
            )}
          </div>
          {!isPast && (
            <Row $gap="10px">
              {summary.paymentBlocked ? (
                <PrimaryButton
                  onClick={async () => {
                    if (!summary.paymentDueId) return
                    setPaying(true)
                    setPayError(null)
                    try {
                      await shopSvc.payDue(currentProfile, summary.paymentDueId, {
                        onSuccess: () => refresh && refresh(),
                      })
                    } catch (e) {
                      setPayError(e?.message || 'Could not start payment.')
                    } finally {
                      setPaying(false)
                    }
                  }}
                  disabled={paying}
                  style={{ background: '#C24D49' }}
                >
                  {paying
                    ? 'Opening…'
                    : summary.paymentAmountMinor && summary.paymentAmountMinor > 0
                      ? `Pay ${formatPaymentAmount(summary.paymentAmountMinor, summary.paymentCurrency)}`
                      : 'Pay'}
                </PrimaryButton>
              ) : canJoin ? (
                <PrimaryButton
                  onClick={() =>
                    navigate(
                      summary.joinUrl ||
                        (summary.slug ? `/l/${summary.slug}` : `/l/${summary.eventId}`),
                    )
                  }
                >
                  Join now
                </PrimaryButton>
              ) : null}
              {summary.canManage && (
                <>
                  <GhostButton onClick={() => setReschedOpen(true)}>Reschedule</GhostButton>
                  <GhostButton
                    onClick={() => setCancelOpen(true)}
                    style={{ borderColor: '#FBEAE9', color: '#C24D49' }}
                  >
                    Cancel
                  </GhostButton>
                </>
              )}
            </Row>
          )}
        </Row>

        {tabs.length > 1 && (
          <TabBar role="tablist">
            {tabs.map((t) => (
              <TabBtn
                key={t.id}
                $active={t.id === activeTab}
                onClick={() => setTab(t.id)}
                role="tab"
                aria-selected={t.id === activeTab}
              >
                {t.label}
              </TabBtn>
            ))}
          </TabBar>
        )}

        <Sheet>
          {activeTab === 'overview' && (
            <Stack $gap="12px">
              {detail.summaryNote && (
                <p style={{ margin: 0, lineHeight: 1.6, color: '#1F2937' }}>
                  {detail.summaryNote}
                </p>
              )}
              {summary.agendaPrompt && (
                <div
                  style={{
                    background: '#F4F0E9',
                    padding: '12px 14px',
                    borderRadius: 12,
                    fontStyle: 'italic',
                    color: '#4B5563',
                  }}
                >
                  "{summary.agendaPrompt}"
                </div>
              )}
              {!isPast && !detail.summaryNote && !summary.agendaPrompt && (
                <Muted>The recap will appear here after your session.</Muted>
              )}
            </Stack>
          )}

          {activeTab === 'prescription' && (
            <Stack $gap="12px">
              <Muted>
                Prescription <code>{detail.prescriptionId}</code> — full Rx viewer arrives in F6
                (Records). For now, the consolidated view at{' '}
                <button
                  onClick={() => navigate('/patient-home/records/prescriptions')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#1F3A5F',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    fontSize: 14,
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  Records → Prescriptions
                </button>{' '}
                lists every Rx across your sessions.
              </Muted>
            </Stack>
          )}

          {activeTab === 'notes' && (
            <Stack $gap="12px">
              <Muted>
                Notes shared by your practitioner for this session. Full notes
                viewer arrives in F6.
              </Muted>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {(detail.sharedNoteIds || []).map((id) => (
                  <li key={id}>{id}</li>
                ))}
              </ul>
            </Stack>
          )}

          {activeTab === 'receipt' && (
            <Stack $gap="12px">
              <Muted>
                Tap below to download the receipt as a PDF. A preview also opens in a side
                drawer for a quick look before saving.
              </Muted>
              <Row $gap="10px" $wrap>
                <PrimaryButton
                  onClick={() => receiptDrawer.open(currentProfile, summary)}
                  disabled={receiptDrawer.state.loading}
                >
                  {receiptDrawer.state.loading ? 'Preparing…' : '⬇  Download receipt'}
                </PrimaryButton>
              </Row>
              {receiptDrawer.state.error ? (
                <Muted style={{ color: '#C24D49' }}>{receiptDrawer.state.error}</Muted>
              ) : null}
            </Stack>
          )}

          {activeTab === 'feedback' && (
            <Stack $gap="12px">
              {detail.feedback ? (
                <>
                  <Muted>
                    You rated this session{' '}
                    <strong style={{ color: '#1F2937' }}>{detail.feedback.rating}/5</strong>.
                  </Muted>
                  {detail.feedback.text && (
                    <p style={{ margin: 0, lineHeight: 1.6 }}>
                      "{detail.feedback.text}"
                    </p>
                  )}
                  <GhostButton onClick={() => setFeedbackOpen(true)}>Update feedback</GhostButton>
                </>
              ) : (
                <>
                  <Muted>You haven&apos;t rated this session yet.</Muted>
                  <PrimaryButton onClick={() => setFeedbackOpen(true)}>Rate session</PrimaryButton>
                </>
              )}
            </Stack>
          )}
        </Sheet>
      </Stack>

      <RescheduleDialog
        open={reschedOpen}
        session={summary}
        onClose={() => setReschedOpen(false)}
        onSubmit={async (body) => {
          await reschedule(body)
        }}
      />
      <CancelDialog
        open={cancelOpen}
        session={summary}
        onClose={() => setCancelOpen(false)}
        onSubmit={async (body) => {
          await doCancel(body)
          navigate('/patient-home/sessions/upcoming')
        }}
      />
      <FeedbackDialog
        open={feedbackOpen}
        session={summary}
        existing={detail.feedback}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={async (body) => {
          await submitFeedback(body)
          refresh()
        }}
      />
      <ReceiptDrawer
        open={receiptDrawer.state.open}
        title={receiptDrawer.state.title || 'Receipt'}
        subtitle={receiptDrawer.state.subtitle}
        blobUrl={receiptDrawer.state.blobUrl}
        mimeType={receiptDrawer.state.mimeType}
        filename={receiptDrawer.state.filename}
        fallbackText={receiptDrawer.state.fallbackText}
        onClose={receiptDrawer.close}
      />
    </ContentMaxWidth>
  )
}
